/**
 * The studio's slots and cells (Phase 91, 91-08; UI-SPEC [B]). Pure, no React.
 *
 * getProduct answers one slot per platen x side x tone. The screen groups
 * them: a GROUP is one (side, tone), the file it prints from (the slot
 * control picks it); a CELL is one group x platen, one placement (a platen
 * tab). Groups run front then back, tones single, light, medium, dark;
 * platens smallest first. Save and next walks cells in that order.
 */
import { DTG_PLATEN_CODES } from '@threadheads/shared';
import type { StudioSlot } from '../hooks/useStudioProduct';

export type StudioSide = StudioSlot['side'];
export type StudioTone = StudioSlot['tone'];
export type StudioPlaten = StudioSlot['platen'];

/** One (side, tone): the file a group of platens prints from. */
export type GroupKey = `${StudioSide}|${StudioTone}`;
/** One group x platen: one placement. */
export type CellKey = `${GroupKey}|${StudioPlaten}`;

/** Every group the studio can hold, in walk order (the screen keeps one upload per group). */
export const STUDIO_GROUP_KEYS = [
  'front|single',
  'front|light',
  'front|medium',
  'front|dark',
  'back|single',
  'back|light',
  'back|medium',
  'back|dark',
] as const satisfies readonly GroupKey[];

export interface StudioGroup {
  key: GroupKey;
  side: StudioSide;
  tone: StudioTone;
  /** The group's slots, smallest platen first. */
  slots: StudioSlot[];
}

export function groupKeyOf(slot: Pick<StudioSlot, 'side' | 'tone'>): GroupKey {
  return `${slot.side}|${slot.tone}`;
}

export function cellKeyOf(slot: Pick<StudioSlot, 'side' | 'tone' | 'platen'>): CellKey {
  return `${slot.side}|${slot.tone}|${slot.platen}`;
}

const groupRank = (key: GroupKey) => STUDIO_GROUP_KEYS.indexOf(key);
const platenRank = (platen: StudioPlaten) => (DTG_PLATEN_CODES as readonly string[]).indexOf(platen);

/** The product's slots as groups, in walk order. */
export function studioGroups(slots: readonly StudioSlot[]): StudioGroup[] {
  const byKey = new Map<GroupKey, StudioGroup>();
  for (const slot of slots) {
    const key = groupKeyOf(slot);
    let group = byKey.get(key);
    if (!group) {
      group = { key, side: slot.side, tone: slot.tone, slots: [] };
      byKey.set(key, group);
    }
    group.slots.push(slot);
  }
  const groups = [...byKey.values()].sort((a, b) => groupRank(a.key) - groupRank(b.key));
  for (const group of groups) group.slots.sort((a, b) => platenRank(a.platen) - platenRank(b.platen));
  return groups;
}

/** Every cell key in walk order: groups in order, platens smallest first. */
export function walkOrder(groups: readonly StudioGroup[]): CellKey[] {
  return groups.flatMap((g) => g.slots.map(cellKeyOf));
}

const SIDE_LABEL = { front: 'slot.front', back: 'slot.back' } as const;
const SIDE_TONE_LABEL: Record<StudioSide, Record<StudioTone, string>> = {
  front: { single: 'slot.front', light: 'slot.frontLight', medium: 'slot.frontMedium', dark: 'slot.frontDark' },
  back: { single: 'slot.back', light: 'slot.backLight', medium: 'slot.backMedium', dark: 'slot.backDark' },
};

/**
 * The slot control's label key: the side alone when the product has one
 * group on that side ("Back"), else side and tone ("Front light").
 */
export function groupLabelKey(group: Pick<StudioGroup, 'side' | 'tone'>, groups: readonly Pick<StudioGroup, 'side'>[]): string {
  const onSide = groups.filter((g) => g.side === group.side).length;
  return onSide > 1 ? SIDE_TONE_LABEL[group.side][group.tone] : SIDE_LABEL[group.side];
}
